/* eslint-disable @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-return */
import * as z from 'zod'
import { csrfTokenService } from '../csrf/CsrfTokenService'
import { UserEmail, userEmailSchema } from './repositories'

export const userEmailSchema = z.object({
  id: z.string().optional(),
  email: z.string(),
  verified: z.boolean(),
  isConfirmationSent: z.boolean().optional(),
})

export type UserEmail = z.infer<typeof userEmailSchema>
export class UserPendingEmailConfirmationsRepositoryFetch {
  async get(): Promise<Record<string, UserEmail>> {
    let response: ResponseEmail[] = []
    try {
      response = await $fetch<ResponseEmail[]>(this.fetchUrl)
    } catch {
      return {}
    }
    return this.mapResponse(response)
  }

  async resendConfirmation(emailId: string): Promise<string> {
    const _response = await fetch(
      `/_/fallback/accounts/resend_confirmation_email/${emailId}/?json=1`,
      {
        method: 'POST',
        body: null,
        headers: {
          'X-CSRFToken': await this.getCsrfToken(),
        },
      },
    )
    return Promise.resolve(emailId)
  }

  private mapResponse(results: ResponseEmail[]): Record<string, UserEmail> {
    return results.reduce((acc, email) => {
      const e = {
        ...email,
        id: String(email.id),
      }
      acc[email.email] = userEmailSchema.parse(e)
      return acc
    }, {})
  }

  async remove(email: string): Promise<void> {
    const params = new URLSearchParams({ email })

    const _response = await fetch(`${this.fetchUrl}?${params.toString()}`, {
      method: 'DELETE',
      headers: {
        'X-CSRFToken': await this.getCsrfToken(),
      },
    })
  }

  async add(email: string): Promise<void> {
    const body = new FormData()
    body.append('email', email)
    const response = await fetch(this.fetchUrl, {
      method: 'POST',
      body,
      headers: {
        'X-CSRFToken': await this.getCsrfToken(),
      },
    })

    const data = await response.json()
    if (response.status !== 200) {
      throw new Error(JSON.stringify(data.errors as AddResponseErrors))
    } else {
      return data
    }
  }

  private get fetchUrl() {
    return '/_/fallback/_/users/user-emails/'
  }

  private async getCsrfToken() {
    const { csrfToken } = await csrfTokenService()

    return csrfToken
  }
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars -- used as type reference only
const responseEmailSchema = z.object({
  id: z.number(),
  email: z.string(),
  verified: z.boolean(),
})

type ResponseEmail = z.infer<typeof responseEmailSchema>

type Error = string[]

interface AddResponseErrors {
  email?: Error
}
